'use client';

import { Box, Link, ListItem } from '@coolblue-development/freddy';
import selectPropertyTrigger from '@coolblue-development/ga4-events/triggers/AlternativePrimaryItem/selectPropertyTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { PropertyExplanationModal } from '@coolblue-development/next-hemingway/client';
import type { DistinctProperty } from '@features/product/typings';
import { useState } from 'react';

import { stickyTitleStyles } from '../BetterOptionsBlock.styled';
import PropertyValueRow from './PropertyValueRow';

type Props = {
  displayName: string;
  values: DistinctProperty[];
  productTypeId: number;
};

const PropertyRow = ({ displayName, values, productTypeId }: Props) => {
  const { trackEvent } = useAnalytics();

  const [showPropertyExplanation, setShowPropertyExplanation] = useState<boolean>(() => false);

  const params = {
    property_name: displayName,
    property_value: values.join(', ')
  };

  const hasExplanation = values[0].hasExplanation ?? false;

  function togglePropertyExplanationModal(open: boolean) {
    if (!hasExplanation || open === showPropertyExplanation) return;
    setShowPropertyExplanation(open);
  }

  return (
    <>
      <ListItem
        sx={theme => ({
          borderTop: `${theme.borderWidth[25]} solid ${theme.color.border.base}`
        })}
      >
        <Box
          py={200}
          px={300}
          sx={theme => ({
            position: 'relative',
            '&:before': {
              width: `calc(100% / ${values.length})`,
              height: '100%',
              position: 'absolute',
              left: 0,
              top: 0,
              backgroundColor: theme.color.background.secondary,
              content: '""'
            }
          })}
        >
          {hasExplanation ? (
            <Link
              fontWeight={700}
              onClick={() => {
                trackEvent(selectPropertyTrigger(params));
                togglePropertyExplanationModal(true);
              }}
              sx={theme => ({
                ...stickyTitleStyles,
                display: 'inline-block',
                maxWidth: `calc(calc(100% / ${values.length}) - ${theme.spacing[200]})`,
                width: '100%'
              })}
            >
              {displayName}
            </Link>
          ) : (
            <Box
              sx={theme => ({
                ...stickyTitleStyles,
                display: 'inline-block',
                maxWidth: `calc(calc(100% / ${values.length}) - ${theme.spacing[200]})`,
                width: '100%',
                fontWeight: 700
              })}
            >
              {displayName}
            </Box>
          )}
        </Box>

        <PropertyValueRow values={values} />
      </ListItem>
      {hasExplanation && (
        <PropertyExplanationModal
          open={showPropertyExplanation}
          onClose={() => togglePropertyExplanationModal(false)}
          property={values[0]}
          productTypeId={productTypeId.toString()}
        />
      )}
    </>
  );
};

export default PropertyRow;
